import React, { useState } from "react";
import { Share2, Copy, MessageCircle, Send, Mail, X, Check, Facebook } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * ShareButton — opens native share sheet on capable devices,
 * falls back to a compact custom sheet with WhatsApp / Telegram / SMS / Email / Copy.
 *
 * Props:
 *  - url?: string          Absolute or path (defaults to current page)
 *  - title?: string
 *  - text?: string
 *  - variant?: "primary" | "ghost" | "icon" | "chip"
 *  - label?: string
 *  - testIdSuffix?: string
 */
export default function ShareButton({
  url,
  title = "Check this out on Find2Near",
  text = "I found this on Find2Near — thought you'd like it 👇",
  variant = "ghost",
  label = "Share",
  testIdSuffix = "default",
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const absoluteUrl = (() => {
    if (!url) return typeof window !== "undefined" ? window.location.href : "";
    if (/^https?:\/\//i.test(url)) return url;
    return typeof window !== "undefined" ? window.location.origin + (url.startsWith("/") ? url : `/${url}`) : url;
  })();

  const shareText = `${text}\n${absoluteUrl}`;
  const encTitle = encodeURIComponent(title);
  const encUrl = encodeURIComponent(absoluteUrl);
  const encText = encodeURIComponent(shareText);

  // Log every share attempt to /api/rewards/share/log so eligible users earn +1 pt per
  // unique destination (WhatsApp / Telegram / SMS / Email / Facebook / Copy / native)
  // and +100 pt milestone at 10 shares. Silent failure — non-critical to the share flow.
  const logShare = (destination) => {
    api.post("/rewards/share/log", { destination, target_url: absoluteUrl }).catch(() => {});
  };

  const openNative = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url: absoluteUrl });
        logShare("native");
        return true;
      } catch (e) {
        // User cancelled or blocked — fall back to sheet.
        return false;
      }
    }
    return false;
  };

  const onClick = async () => {
    const done = await openNative();
    if (!done) setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      logShare("copy_link");
      setCopied(true);
      toast.success("Link copied · +1 point earned");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — long-press to copy manually");
    }
  };

  const chips = [
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#25D366", href: `https://wa.me/?text=${encText}` },
    { key: "telegram", label: "Telegram", icon: Send, color: "#26A5E4", href: `https://t.me/share/url?url=${encUrl}&text=${encTitle}` },
    { key: "sms", label: "SMS", icon: MessageCircle, color: "#2D5A27", href: `sms:?&body=${encText}` },
    { key: "email", label: "Email", icon: Mail, color: "#E05E36", href: `mailto:?subject=${encTitle}&body=${encText}` },
    { key: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2", href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}` },
  ];

  const btnClass = (() => {
    if (variant === "primary") return "rounded-full bg-[#2D5A27] hover:bg-[#24491f] text-white gap-2";
    if (variant === "icon") return "w-10 h-10 rounded-full grid place-items-center bg-white border border-stone-200 hover:border-stone-300 text-stone-800";
    if (variant === "chip") return "inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-stone-50 border border-stone-200 text-stone-800 px-3 py-1.5 text-xs font-bold";
    return "rounded-full bg-white hover:bg-stone-50 border border-stone-200 text-stone-800 gap-2";
  })();

  return (
    <>
      {variant === "icon" ? (
        <button
          onClick={onClick}
          className={btnClass}
          data-testid={`share-btn-${testIdSuffix}`}
          aria-label={label}
          title={label}
        >
          <Share2 size={16} />
        </button>
      ) : variant === "chip" ? (
        <button onClick={onClick} className={btnClass} data-testid={`share-btn-${testIdSuffix}`}>
          <Share2 size={13} /> {label}
        </button>
      ) : (
        <Button onClick={onClick} className={btnClass} data-testid={`share-btn-${testIdSuffix}`}>
          <Share2 size={16} /> {label}
        </Button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[70] bg-stone-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setOpen(false)}
          data-testid={`share-sheet-${testIdSuffix}`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl border border-stone-200 shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-5 border-b border-stone-100">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Share via</div>
                <div className="heading font-extrabold text-stone-900 text-lg mt-0.5 line-clamp-1">{title}</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full hover:bg-stone-100 grid place-items-center text-stone-600"
                data-testid={`share-close-${testIdSuffix}`}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-5 gap-2 p-5">
              {chips.map((c) => {
                const Icon = c.icon;
                return (
                  <a
                    key={c.key}
                    href={c.href}
                    target={c.key === "sms" || c.key === "email" ? "_self" : "_blank"}
                    rel="noopener noreferrer"
                    onClick={() => { logShare(c.key); setOpen(false); }}
                    className="flex flex-col items-center gap-2 py-3 rounded-2xl hover:bg-stone-50 transition-colors"
                    data-testid={`share-${c.key}-${testIdSuffix}`}
                  >
                    <div className="w-12 h-12 rounded-full grid place-items-center text-white" style={{ backgroundColor: c.color }}>
                      <Icon size={20} />
                    </div>
                    <div className="text-[11px] font-bold text-stone-700">{c.label}</div>
                  </a>
                );
              })}
            </div>

            <div className="px-5 pb-5 pt-1">
              <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-2xl px-3 py-2">
                <div className="text-xs text-stone-600 truncate flex-1" title={absoluteUrl}>{absoluteUrl}</div>
                <button
                  onClick={copy}
                  className="text-xs font-bold text-white bg-[#E05E36] hover:bg-[#c94e2a] rounded-full px-3 py-1.5 flex items-center gap-1.5 shrink-0"
                  data-testid={`share-copy-${testIdSuffix}`}
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="text-[11px] text-stone-500 mt-3 text-center">
                Share to <b className="text-[#E05E36]">10 places</b> and earn a <b>+100 point bonus</b> — every unique destination gives you +1 point.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
